import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useCallback } from 'react';

export type ApprovalStatus =
  | 'pending'
  | 'rejected'
  | 'processing'
  | 'completed'
  | 'failed';

export interface ApprovalRequest {
  id: string;
  kind: string;
  requester: string;
  status: ApprovalStatus;
  payload: Record<string, unknown>;
  approverGroups: string[];
  approverUsers: string[];
  title: string;
  summary: string;
  createdAt: string;
  updatedAt: string;
  approver?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  resultUrl?: string;
  error?: string;
}

export function useApprovalsApi() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const base = useCallback(
    () => discoveryApi.getBaseUrl('approvals'),
    [discoveryApi],
  );

  const list = useCallback(
    async (params: {
      kind?: string;
      status?: string;
      /** Restrict to these ids (the Archived view fetches its archived ids). */
      ids?: string[];
      limit: number;
      offset: number;
    }) => {
      const baseUrl = await base();
      const sp = new URLSearchParams({
        limit: String(params.limit),
        offset: String(params.offset),
      });
      if (params.kind && params.kind !== 'all') sp.set('kind', params.kind);
      if (params.status && params.status !== 'all')
        sp.set('status', params.status);
      params.ids?.forEach(id => sp.append('id', id));
      const res = await fetchApi.fetch(`${baseUrl}/requests?${sp}`);
      if (!res.ok) throw new Error(await res.text());
      return res.json() as Promise<{
        requests: ApprovalRequest[];
        total: number;
      }>;
    },
    [base, fetchApi],
  );

  const get = useCallback(
    async (id: string) => {
      const baseUrl = await base();
      const res = await fetchApi.fetch(`${baseUrl}/requests/${id}`);
      if (!res.ok) throw new Error(await res.text());
      return (await res.json()).request as ApprovalRequest;
    },
    [base, fetchApi],
  );

  const act = useCallback(
    async (id: string, action: 'approve' | 'reject', reason?: string) => {
      const baseUrl = await base();
      const res = await fetchApi.fetch(`${baseUrl}/requests/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: action === 'reject' ? JSON.stringify({ reason }) : undefined,
      });
      if (!res.ok) throw new Error(await res.text());
      return (await res.json()).request as ApprovalRequest;
    },
    [base, fetchApi],
  );

  // Dismiss a stale pending request. The backend deletes it ONLY after a
  // server-side check confirms the underlying object (e.g. GitHub release) is
  // gone; if it still exists the backend replies 409, surfaced here as an error
  // carrying `.status` so the caller can distinguish "still exists" from a real
  // failure.
  const dismiss = useCallback(
    async (id: string) => {
      const baseUrl = await base();
      const res = await fetchApi.fetch(`${baseUrl}/requests/${id}/dismiss`, {
        method: 'POST',
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw Object.assign(
          new Error(text || `dismiss failed: ${res.status}`),
          { status: res.status },
        );
      }
    },
    [base, fetchApi],
  );

  const kinds = useCallback(async () => {
    const baseUrl = await base();
    const res = await fetchApi.fetch(`${baseUrl}/kinds`);
    if (!res.ok) throw new Error(await res.text());
    // response shape: { kinds: [{ kind, label }] }
    return ((await res.json()).kinds ?? []) as Array<{
      kind: string;
      label: string;
    }>;
  }, [base, fetchApi]);

  return { list, get, act, dismiss, kinds };
}
